import { UNSUPPORTED_LIST_URLS } from './../../constants/lists'
import DEFAULT_TOKEN_LIST from '../../constants/default-token-list.json'
import { useState, useEffect, useMemo } from 'react'
import { ChainId, Token } from '@uniswap/sdk'
import { Tags, TokenInfo, TokenList } from '@uniswap/token-lists'
import { useSelector } from 'react-redux'
import { AppState } from '../index'
import sortByListPriority from 'utils/listSort'
import UNSUPPORTED_TOKEN_LIST from '../../constants/tokenLists/uniswap-v2-unsupported.tokenlist.json'

const DEFAULT_TOKENS = 'https://raw.githubusercontent.com/inquisitiveScholar/dex-tokesns/main/poodl-tokenlist.json'

// Cache for fetched token lists
const tokenListCache = new Map<string, TokenList>()

type TagDetails = Tags[keyof Tags]
export interface TagInfo extends TagDetails {
  id: string
}

export class WrappedTokenInfo extends Token {
  public readonly tokenInfo: TokenInfo
  public readonly tags: TagInfo[]
  constructor(tokenInfo: TokenInfo, tags: TagInfo[]) {
    super(tokenInfo.chainId, tokenInfo.address, tokenInfo.decimals, tokenInfo.symbol, tokenInfo.name)
    this.tokenInfo = tokenInfo
    this.tags = tags
  }
  public get logoURI(): string | undefined {
    return this.tokenInfo.logoURI
  }
}

export type TokenAddressMap = Readonly<
  { [chainId in ChainId]: Readonly<{ [tokenAddress: string]: { token: WrappedTokenInfo; list: TokenList } }> }
>

const EMPTY_LIST: TokenAddressMap = {
  [ChainId.KOVAN]: {},
  [ChainId.RINKEBY]: {},
  [ChainId.ROPSTEN]: {},
  [ChainId.GÖRLI]: {},
  [ChainId.MAINNET]: {}
}

// Strong cache for token maps
const listCache: WeakMap<TokenList, TokenAddressMap> = new WeakMap()

export function listToTokenMap(list: TokenList): TokenAddressMap {
  const cached = listCache.get(list)
  if (cached) return cached

  const map = list.tokens.reduce<TokenAddressMap>(
    (tokenMap, tokenInfo) => {
      const tags: TagInfo[] =
        tokenInfo.tags
          ?.map(tagId => {
            if (!list.tags?.[tagId]) return undefined
            return { ...list.tags[tagId], id: tagId }
          })
          ?.filter((x): x is TagInfo => Boolean(x)) ?? []
      
      const token = new WrappedTokenInfo(tokenInfo, tags)
      
      if (tokenMap[token.chainId][token.address] !== undefined) {
        console.warn(`Duplicate token skipped: ${token.address}`)
        return tokenMap
      }
      
      return {
        ...tokenMap,
        [token.chainId]: {
          ...tokenMap[token.chainId],
          [token.address]: {
            token,
            list: list
          }
        }
      }
    },
    { ...EMPTY_LIST }
  )
  
  listCache.set(list, map)
  return map
}

export function useAllLists() {
  return useSelector<AppState, AppState['lists']['byUrl']>(state => state.lists.byUrl)
}

function combineMaps(map1: TokenAddressMap, map2: TokenAddressMap): TokenAddressMap {
  return {
    15259: { ...map1[15259], ...map2[15259] },
    3: { ...map1[3], ...map2[3] },
    4: { ...map1[4], ...map2[4] },
    5: { ...map1[5], ...map2[5] },
    42: { ...map1[42], ...map2[42] }
  }
}

// Custom hook for fetching token lists with caching
function useTokenList(url: string): TokenList | null {
  const [list, setList] = useState<TokenList | null>(null)

  useEffect(() => {
    const fetchList = async () => {
      if (tokenListCache.has(url)) {
        setList(tokenListCache.get(url)!)
        return
      }

      try {
        const response = await fetch(url)
        if (!response.ok) throw new Error('Failed to fetch token list')
        
        const data: TokenList = await response.json()
        tokenListCache.set(url, data)
        setList(data)
      } catch (error) {
        console.error('Error fetching token list:', error)
        // Fallback to empty list
        setList({
          name: "Fallback List",
          timestamp: new Date().toISOString(),
          version: { major: 1, minor: 0, patch: 0 },
          tokens: []
        })
      }
    }

    fetchList()
  }, [url])

  return list
}

function useCombinedTokenMapFromUrls(urls: string[] | undefined): TokenAddressMap {
  const lists = useAllLists()

  return useMemo(() => {
    if (!urls) return EMPTY_LIST

    return urls
      .slice()
      .sort(sortByListPriority)
      .reduce((allTokens, currentUrl) => {
        const current = lists[currentUrl]?.current
        if (!current) return allTokens
        
        try {
          const newTokens = listToTokenMap(current)
          return combineMaps(allTokens, newTokens)
        } catch (error) {
          console.error('Could not show token list due to error', error)
          return allTokens
        }
      }, EMPTY_LIST)
  }, [lists, urls])
}

export function useActiveListUrls(): string[] | undefined {
  return useSelector<AppState, AppState['lists']['activeListUrls']>(state => state.lists.activeListUrls)?.filter(
    url => !UNSUPPORTED_LIST_URLS.includes(url)
  )
}

export function useInactiveListUrls(): string[] {
  const lists = useAllLists()
  const allActiveListUrls = useActiveListUrls()
  return Object.keys(lists).filter(url => !allActiveListUrls?.includes(url) && !UNSUPPORTED_LIST_URLS.includes(url))
}

export function useCombinedActiveList(): TokenAddressMap {
  const tokenList = useTokenList(DEFAULT_TOKENS)
  const activeListUrls = useActiveListUrls()
  const activeTokens = useCombinedTokenMapFromUrls(activeListUrls)

  return useMemo(() => {
    if (!tokenList) return activeTokens
    const defaultTokenMap = listToTokenMap(tokenList)
    return combineMaps(activeTokens, defaultTokenMap)
  }, [tokenList, activeTokens])
}

export function useCombinedInactiveList(): TokenAddressMap {
  const allInactiveListUrls: string[] = useInactiveListUrls()
  return useCombinedTokenMapFromUrls(allInactiveListUrls)
}

export function useDefaultTokenList(): TokenAddressMap {
  const tokenList = useTokenList(DEFAULT_TOKENS)
  return useMemo(() => tokenList ? listToTokenMap(tokenList) : EMPTY_LIST, [tokenList])
}

export function useUnsupportedTokenList(): TokenAddressMap {
  const localUnsupportedListMap = useMemo(() => listToTokenMap(UNSUPPORTED_TOKEN_LIST), [])
  const loadedUnsupportedListMap = useCombinedTokenMapFromUrls(UNSUPPORTED_LIST_URLS)

  return useMemo(() => 
    combineMaps(localUnsupportedListMap, loadedUnsupportedListMap),
    [localUnsupportedListMap, loadedUnsupportedListMap]
  )
}

export function useIsListActive(url: string): boolean {
  const activeListUrls = useActiveListUrls()
  return Boolean(activeListUrls?.includes(url))
}